import { FC, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Flex, Typography } from 'antd';
import { TbDatabase, TbTextRecognition } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import OverflowFadeScrollArea from '@/shared/components/layouts/OverflowFadeScrollArea';
import CompactCollapsible from '@/shared/components/layouts/sections/CompactCollapsible';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import { BaseSkeleton } from '@/shared/components/skeletons';
import SmallTag from '@/shared/components/tags/SmallTag';
import { ResearchDomainDefinition } from '@/shared/document-intelligence/researchOntology';

import { DocumentConceptIcon } from './DocumentConceptTag';
import { DocumentConcept, DocumentConceptReference, DocumentIntelligence } from './documentIntelligence';

interface DocumentConceptSiderProps {
    activeReferenceId?: string;
    intelligence: DocumentIntelligence;
    loading: boolean;
    referenceLocationLabel?: (reference: DocumentConceptReference) => string;
    textExtractionSupported: boolean;
    onReferenceSelect: (reference: DocumentConceptReference) => void;
}

const renderConceptContext = (context: string, concept: string, color: string): ReactNode => {
    const escapedConcept = concept.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = context.split(new RegExp(`(${escapedConcept})`, 'gi'));

    return parts.map((part, index) =>
        part.localeCompare(concept, undefined, { sensitivity: 'accent' }) === 0 ? (
            <strong key={`${part}:${index}`} style={{ color }}>
                {part}
            </strong>
        ) : (
            <Fragment key={`${part}:${index}`}>{part}</Fragment>
        ),
    );
};

interface DocumentConceptGroup {
    concepts: DocumentConcept[];
    description: string;
    domain?: ResearchDomainDefinition;
    id: string;
    label: string;
    order: number;
}

const MAPPING_VERIFICATION_LABELS = {
    declared: 'Declared mapping',
    'source-verified': 'Source verified',
    stale: 'Verification stale',
    unavailable: 'Source unavailable',
} as const;

const groupDocumentConcepts = (
    concepts: DocumentConcept[],
    domains: readonly ResearchDomainDefinition[],
): DocumentConceptGroup[] => {
    const groups = new Map<string, DocumentConceptGroup>();
    const domainById = new Map(domains.map((domain) => [domain.id, domain]));

    concepts.forEach((concept) => {
        const domain = concept.primaryDomainId ? domainById.get(concept.primaryDomainId) : undefined;
        const id = domain?.id ?? 'document-specific';
        const existing = groups.get(id);

        if (existing) {
            existing.concepts.push(concept);
            return;
        }

        groups.set(id, {
            concepts: [concept],
            description: domain?.description ?? 'High-frequency concepts identified specifically within this document.',
            domain,
            id,
            label: domain?.label ?? 'Document-Specific',
            order: domain?.order ?? Number.MAX_SAFE_INTEGER,
        });
    });

    return [...groups.values()].sort(
        (left, right) => left.order - right.order || left.label.localeCompare(right.label),
    );
};

const findActiveConceptLocation = (groups: DocumentConceptGroup[], activeReferenceId?: string) => {
    if (!activeReferenceId) {
        return undefined;
    }

    for (const group of groups) {
        for (const concept of group.concepts) {
            if (concept.references.some((reference) => reference.id === activeReferenceId)) {
                return { concept, group };
            }
        }
    }

    return undefined;
};

const DocumentConceptSider: FC<DocumentConceptSiderProps> = ({
    activeReferenceId,
    intelligence,
    loading,
    onReferenceSelect,
    referenceLocationLabel = (reference) => `p. ${reference.page}`,
    textExtractionSupported,
}) => {
    const { token } = useAntDesignTheme();
    const conceptGroups = useMemo(
        () => groupDocumentConcepts(intelligence.concepts, intelligence.domains),
        [intelligence.concepts, intelligence.domains],
    );
    const schemeById = useMemo(
        () => new Map(intelligence.schemes.map((scheme) => [scheme.id, scheme])),
        [intelligence.schemes],
    );
    const referenceElements = useRef(new Map<string, HTMLAnchorElement | HTMLButtonElement>());
    const activeLocation = findActiveConceptLocation(conceptGroups, activeReferenceId);
    const activeDomainId = activeLocation?.group.id;
    const activeConceptId = activeLocation?.concept.id;
    const [openDomainIds, setOpenDomainIds] = useState<ReadonlySet<string>>(() =>
        activeDomainId ? new Set([activeDomainId]) : new Set(),
    );
    const [openConceptIds, setOpenConceptIds] = useState<ReadonlySet<string>>(() =>
        activeConceptId ? new Set([activeConceptId]) : new Set(),
    );
    const activeDomainOpen = Boolean(activeDomainId && openDomainIds.has(activeDomainId));
    const activeConceptOpen = Boolean(activeConceptId && openConceptIds.has(activeConceptId));

    useEffect(() => {
        if (!activeReferenceId || !activeDomainOpen || !activeConceptOpen) {
            return;
        }

        let innerFrame = 0;
        const outerFrame = window.requestAnimationFrame(() => {
            innerFrame = window.requestAnimationFrame(() => {
                referenceElements.current
                    .get(activeReferenceId)
                    ?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
            });
        });

        return () => {
            window.cancelAnimationFrame(outerFrame);
            window.cancelAnimationFrame(innerFrame);
        };
    }, [activeConceptOpen, activeDomainOpen, activeReferenceId]);

    return (
        <div
            aria-label='Concepts'
            style={{
                display: 'flex',
                flex: '1 1 0%',
                flexDirection: 'column',
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
            }}
        >
            <OverflowFadeScrollArea
                aria-label='Document concepts'
                containerStyle={{ flex: '1 1 0%', minHeight: 0 }}
                surface='container'
            >
                {loading ? (
                    <div style={{ padding: '0.75rem' }}>
                        <BaseSkeleton active round paragraph={{ rows: 8 }} />
                    </div>
                ) : !textExtractionSupported ? (
                    <EmptyView
                        description='Text recognition for raster images requires OCR. PDF concepts are extracted locally from the authenticated document.'
                        height='100%'
                        icon={<TbTextRecognition style={{ color: token.colorTextTertiary }} />}
                        label='OCR Required'
                    />
                ) : conceptGroups.length ? (
                    conceptGroups.map((group) => {
                        const domainActive = activeLocation?.group.id === group.id;

                        return (
                            <CompactCollapsible
                                key={group.id}
                                destroyOnHidden
                                description={group.description}
                                headerExtra={
                                    <SmallTag
                                        compact
                                        message={group.concepts.length.toLocaleString()}
                                        styles={{ marginLeft: 0 }}
                                    />
                                }
                                headerStyle={{ background: domainActive ? token.colorFillQuaternary : undefined }}
                                icon={
                                    group.domain ? (
                                        <DocumentConceptIcon domainId={group.domain.id} size='1rem' />
                                    ) : (
                                        <TbDatabase />
                                    )
                                }
                                label={group.label}
                                open={openDomainIds.has(group.id)}
                                contentStyle={{ padding: 0 }}
                                onOpenChange={(nextOpen) =>
                                    setOpenDomainIds((current) => {
                                        const next = new Set(current);

                                        if (nextOpen) {
                                            next.add(group.id);
                                        } else {
                                            next.delete(group.id);
                                        }
                                        return next;
                                    })
                                }
                            >
                                {group.concepts.map((concept) => {
                                    const conceptActive = activeLocation?.concept.id === concept.id;
                                    const mappedSchemes = [
                                        ...new Map(
                                            concept.mappings.map((mapping) => [mapping.schemeId, mapping]),
                                        ).values(),
                                    ];

                                    return (
                                        <CompactCollapsible
                                            key={concept.id}
                                            destroyOnHidden
                                            headerExtra={
                                                <SmallTag
                                                    compact
                                                    message={concept.references.length.toLocaleString()}
                                                    styles={{ marginLeft: 0 }}
                                                />
                                            }
                                            label={concept.label}
                                            open={openConceptIds.has(concept.id)}
                                            contentStyle={{ padding: 0 }}
                                            headerStyle={{
                                                background: conceptActive ? token.colorFillQuaternary : undefined,
                                            }}
                                            icon={<DocumentConceptIcon domainId={concept.primaryDomainId} />}
                                            onOpenChange={(nextOpen) =>
                                                setOpenConceptIds((current) => {
                                                    const next = new Set(current);

                                                    if (nextOpen) {
                                                        next.add(concept.id);
                                                    } else {
                                                        next.delete(concept.id);
                                                    }
                                                    return next;
                                                })
                                            }
                                        >
                                            <div style={{ padding: '0 0.75rem 0.625rem' }}>
                                                <Typography.Text
                                                    type='secondary'
                                                    style={{ display: 'block', fontSize: token.fontSizeSM }}
                                                >
                                                    {concept.description}
                                                </Typography.Text>
                                                {mappedSchemes.length ? (
                                                    <Flex gap='0.25rem' wrap style={{ marginTop: '0.5rem' }}>
                                                        {mappedSchemes.map((mapping) => {
                                                            const scheme = schemeById.get(mapping.schemeId);
                                                            const verificationLabel =
                                                                MAPPING_VERIFICATION_LABELS[
                                                                    mapping.verification?.status ?? 'declared'
                                                                ];

                                                            return (
                                                                <span
                                                                    key={`${concept.id}:${mapping.schemeId}`}
                                                                    title={`${mapping.relation}: ${mapping.externalId} (${mapping.schemeVersion}) · ${verificationLabel}`}
                                                                >
                                                                    <SmallTag
                                                                        icon={<TbDatabase />}
                                                                        message={`${scheme?.label ?? mapping.schemeId} · ${verificationLabel}`}
                                                                        styles={{
                                                                            alignItems: 'center',
                                                                            display: 'inline-flex',
                                                                            gap: '0.25rem',
                                                                            marginLeft: 0,
                                                                        }}
                                                                    />
                                                                </span>
                                                            );
                                                        })}
                                                    </Flex>
                                                ) : null}
                                            </div>
                                            {concept.references.map((reference, index) => {
                                                const active = activeReferenceId === reference.id;

                                                return (
                                                    <Button
                                                        key={reference.id}
                                                        ref={(element) => {
                                                            if (element) {
                                                                referenceElements.current.set(reference.id, element);
                                                            } else {
                                                                referenceElements.current.delete(reference.id);
                                                            }
                                                        }}
                                                        aria-current={active ? 'true' : undefined}
                                                        block
                                                        type='text'
                                                        onClick={() => onReferenceSelect(reference)}
                                                        style={{
                                                            backgroundColor: active
                                                                ? token.colorFillQuaternary
                                                                : undefined,
                                                            borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                                            borderLeft: active
                                                                ? `3px solid ${token.colorBorder}`
                                                                : '3px solid transparent',
                                                            borderRadius: 0,
                                                            height: 'auto',
                                                            textAlign: 'left',
                                                            whiteSpace: 'normal',
                                                        }}
                                                    >
                                                        <Flex align='flex-start' gap='0.5rem'>
                                                            <SmallTag
                                                                message={referenceLocationLabel(reference)}
                                                                styles={{ flex: 'none', marginLeft: 0 }}
                                                            />
                                                            <span style={{ minWidth: 0 }}>
                                                                <Typography.Text
                                                                    strong={active}
                                                                    style={{ display: 'block' }}
                                                                >
                                                                    Reference {index + 1}
                                                                </Typography.Text>
                                                                <Typography.Text
                                                                    type='secondary'
                                                                    style={{
                                                                        display: 'block',
                                                                        fontSize: token.fontSizeSM,
                                                                    }}
                                                                >
                                                                    {renderConceptContext(
                                                                        reference.context,
                                                                        reference.text,
                                                                        token.colorText,
                                                                    )}
                                                                </Typography.Text>
                                                            </span>
                                                        </Flex>
                                                    </Button>
                                                );
                                            })}
                                        </CompactCollapsible>
                                    );
                                })}
                            </CompactCollapsible>
                        );
                    })
                ) : (
                    <EmptyView
                        description='No high-signal investment or diligence concepts were identified in the extractable text.'
                        height='100%'
                        icon={<TbTextRecognition style={{ color: token.colorTextTertiary }} />}
                        label='No Concepts Found'
                    />
                )}
            </OverflowFadeScrollArea>
        </div>
    );
};

export default DocumentConceptSider;
